import {
  ACTIVE_BLUE_50,
  FILTER_DATE_FORMAT,
  GREY_03,
  NUMBER_OF_DIGITS,
  PURE_BLACK,
  UTILIZATION_CATEGORY_COLORS,
  VIBRANT_BLUE_CODE,
} from '@shared/models/constants';
import { SnackbarV3Service } from '@gea/digital-ui-lib';
import { ProductResponseDTO, TimeCategory } from '@generated-apis/index';
import { TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import { BarChartItem, ChartDataItem, CipProductionInput, FilterParams, PictogramPicture, SensorConfig, TimeUse } from '@shared/models/common.models';
import {
  BERING_LIQUIDE_SIDE,
  BERING_MOTOR,
  BERING_SOLID_SIDE,
  BERING_SPINDLE_TITLE,
  MOTOR_PLATE,
  TOOLTIP_BERING_LIQUIDE_SIDE,
  TOOLTIP_BERING_MOTOR,
  TOOLTIP_BERING_SOLID_SIDE,
  TOOLTIP_BERING_SPINDLE,
  TOOLTIP_MOTOR_PLATE,
  TOOLTIP_VIBRATION_FRAME,
  TOOLTIP_VIBRATION_LEVEL,
  VIBRATION_FRAME,
  VIBRATION_LEVEL_TITLE,
} from '@shared/models/translations';


export const HEALTH_MONITOR_CONFIGS: Record<string, SensorConfig[]> = {
  INTEGRATED_DIRECT_DRIVE_SEPARATOR: [
    {
      id: 1,
      title: VIBRATION_LEVEL_TITLE,
      tooltipInfo: TOOLTIP_VIBRATION_LEVEL,
      sensorKey: 'online__Daschboard_vibration_pegel',
      showImage: false,
    },
    {
      id: 2,
      title: BERING_SPINDLE_TITLE,
      tooltipInfo: TOOLTIP_BERING_SPINDLE,
      sensorKey: 'online__Dashboard_bearing_1',
      showImage: true,
    },
  ],
  DECANTER: [
    {
      id: 1,
      title: VIBRATION_LEVEL_TITLE,
      tooltipInfo: TOOLTIP_VIBRATION_LEVEL,
      sensorKey: 'online__Daschboard_vibration_pegel',
      showImage: false,
    },
    {
      id: 2,
      title: BERING_LIQUIDE_SIDE,
      tooltipInfo: TOOLTIP_BERING_LIQUIDE_SIDE,
      sensorKey: 'online__Dashboard_bearing_1',
      showImage: true,
    },
    {
      id: 3,
      title: BERING_SOLID_SIDE,
      tooltipInfo: TOOLTIP_BERING_SOLID_SIDE,
      sensorKey: 'online__Dashboard_bearing_2',
      showImage: true,
    },
    { id: 4, title: MOTOR_PLATE, tooltipInfo: TOOLTIP_MOTOR_PLATE, sensorKey: 'online__Dashboard_motor', showImage: false },
  ],
  DIRECT_DRIVE_SEPARATOR: [
    {
      id: 1,
      title: VIBRATION_LEVEL_TITLE,
      tooltipInfo: TOOLTIP_VIBRATION_LEVEL,
      sensorKey: 'online__Daschboard_vibration_pegel',
      showImage: false,
    },
    {
      id: 2,
      title: BERING_SPINDLE_TITLE,
      tooltipInfo: TOOLTIP_BERING_SPINDLE,
      sensorKey: 'online__Dashboard_bearing_1',
      showImage: true,
    },
    { id: 3, title: BERING_MOTOR, tooltipInfo: TOOLTIP_BERING_MOTOR, sensorKey: 'online__Dashboard_motor', showImage: true },
  ],
  FLAT_BELT_SEPARATOR: [
    {
      id: 1,
      title: VIBRATION_LEVEL_TITLE,
      tooltipInfo: TOOLTIP_VIBRATION_LEVEL,
      sensorKey: 'online__Daschboard_vibration_pegel',
      showImage: false,
    },
    {
      id: 2,
      title: BERING_SPINDLE_TITLE,
      tooltipInfo: TOOLTIP_BERING_SPINDLE,
      sensorKey: 'online__Dashboard_bearing_1',
      showImage: true,
    },
    { id: 3, title: BERING_MOTOR, tooltipInfo: TOOLTIP_BERING_MOTOR, sensorKey: 'online__Dashboard_motor', showImage: true },
  ],
  GEARBOX_SEPARATOR: [
    {
      id: 1,
      title: VIBRATION_LEVEL_TITLE,
      tooltipInfo: TOOLTIP_VIBRATION_LEVEL,
      sensorKey: 'online__Daschboard_vibration_pegel',
      showImage: false,
    },
    {
      id: 2,
      title: BERING_SPINDLE_TITLE,
      tooltipInfo: TOOLTIP_BERING_SPINDLE,
      sensorKey: 'online__Dashboard_bearing_1',
      showImage: true,
    },
    { id: 3, title: BERING_MOTOR, tooltipInfo: TOOLTIP_BERING_MOTOR, sensorKey: 'online__Dashboard_motor', showImage: true },
  ],
};
export const CODESTRYKE_CONFIG: SensorConfig[] = [
  {
    id: 1,
    title: VIBRATION_FRAME,
    tooltipInfo: TOOLTIP_VIBRATION_FRAME,
    sensorKey: 'Channel3_VRMS_value',
    showImage: false,
  },
  {
    id: 2,
    title: BERING_SPINDLE_TITLE,
    tooltipInfo: TOOLTIP_BERING_SPINDLE,
    sensorKey: 'Channel1_VRMS_value',
    showImage: true,
  },
  {
    id: 3,
    title: BERING_MOTOR,
    tooltipInfo: TOOLTIP_BERING_MOTOR,
    sensorKey: 'Channel2_VRMS_value',
    showImage: true,
  },
];
export const EMPTY_BAR_CHART_DATA: BarChartItem[] = [
  {
    id: 1,
    title: VIBRATION_LEVEL_TITLE,
    tooltipInfo: TOOLTIP_VIBRATION_LEVEL,
    value: 0,
    showImage: false,
  },
  {
    id: 2,
    title: BERING_SPINDLE_TITLE,
    tooltipInfo: TOOLTIP_BERING_SPINDLE,
    value: 0,
    showImage: true,
  },
];

export function showSnackbarError(snackbarService: SnackbarV3Service, summary: string, detail: string): void {
  snackbarService.clear();
  snackbarService.add({
    summary,
    detail,
    severity: 'error',
  });
}

export function calculateTimeUsage(translate: TranslateService, timeUse?: TimeUse) {
  const used = timeUse?.used_capacity ?? 0;
  const free = timeUse?.free_capacity ?? 0;
  const total = used + free;
  if (total === 0) {
    return [
      { name: translate.instant('TIME-USE.UTILIZED-TIME'), value: 0, color: GREY_03 },
      { name: translate.instant('TIME-USE.UNUTILIZED-TIME'), value: 0, color: GREY_03 },
    ];
  }

  return [
    { name: translate.instant('TIME-USE.UTILIZED-TIME'), value: Number(used.toFixed(1)), color: VIBRANT_BLUE_CODE },
    { name: translate.instant('TIME-USE.UNUTILIZED-TIME'), value: Number(free.toFixed(1)), color: ACTIVE_BLUE_50 },
  ];
}
export function formatServiceDate(day?: number | null, month?: number | null, year?: number | null): string | null {
  if (!day || !month || !year) return null;

  const digits = Math.abs(year).toString();

  const convertedYear = digits.length === 2 ? Number(`20${digits}`) : year;

  const dateObj = new Date(convertedYear, month - 1, day);
  return format(dateObj, 'yyyy-MM-dd');
}

export function convertSecondsToHours(seconds: number, decimalPlaces: number = 2): number {
  const hours = (seconds ?? 0) / 3600;
  return safeParse(hours, decimalPlaces) ?? 0;
}

export function mapCipAndProductionData(cipAndProductionEntries: CipProductionInput[] | undefined | null) {
  if (!cipAndProductionEntries?.length) return null;

  const dates: string[] = [];
  const cipValues: number[] = [];
  const productionValues: number[] = [];

  for (const cipAndProduction of cipAndProductionEntries) {
    if (!cipAndProduction.date) continue;
    dates.push(cipAndProduction.date);
    cipValues.push(cipAndProduction.cip ?? 0);
    productionValues.push(cipAndProduction.production ?? 0);
  }
  return { dates, cipValues, productionValues };
}
export function safeParse(value: number | null | undefined, digits: number): number | null {
  if (value === null || value === undefined) return null;
  return parseFloat(value.toFixed(digits));
}

export function safeRound(value: number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  return Math.round(value);
}

export function formatRounded(current: number | undefined, unit: string = ''): string | undefined {
  if (current == null) {
    return undefined;
  }
  const rounded = Math.round(current * 100) / 100;
  return `${rounded}${unit}`;
}

/**
 * Format params date to the format that BE expects
 * @param params - contain machine id, start and end date as JS Dates, which always stores time internally in UTC
 */
export function formatFilterParams(params: FilterParams) {
  // format the dates to the local date of the user as date only without time
  const startDate = format(params.startDate, FILTER_DATE_FORMAT);
  const endDate = format(params.endDate, FILTER_DATE_FORMAT);

  return { startDate: startDate, endDate: endDate };

  //  Do not delete this commented code, when date will switch to UTC just use this instead of the above code
  //  Format the filter params to UTC and then strip the time from the date string to get the date only
  //
  //   //   convert dates to the start of the day (00:00:00) and end of the day (23:59:59) in the user's local time
  //   const startDate = startOfDay(params.startDate);
  //   const endDate = endOfDay(params.endDate);
  //
  //   // convert to locale-independent ISO 8601 UTC format
  //   const startDateUTC = startDate.toISOString();
  //   const endDateUTC = endDate.toISOString();
  //
  //   // strip the time from the date string to get the dates only
  //   const strippedStartDate = startDateUTC.split('T')[0];
  //   const strippedEndDate = endDateUTC.split('T')[0];
  //
  //   return { machineId: params.machineId, startDate: strippedStartDate, endDate: strippedEndDate };
}

const machinePictogramMap: Partial<Record<ProductResponseDTO.MachineTypeEnum, PictogramPicture>> & { default: PictogramPicture } =
  {
    [ProductResponseDTO.MachineTypeEnum.Decanter]: 'x512_DEC_VB.svg',
    [ProductResponseDTO.MachineTypeEnum.FlatBeltSeparator]: 'x512_SEP_BD_VB.svg',
    [ProductResponseDTO.MachineTypeEnum.GearboxSeparator]: 'x512_SEP_GD_VB.svg',
    [ProductResponseDTO.MachineTypeEnum.DirectDriveSeparator]: 'x512_SEP_DD_VB.svg',
    [ProductResponseDTO.MachineTypeEnum.IntegratedDirectDriveSeparator]: 'x512_SEP_IDD_VB.svg',
    default: 'GEA_WFS_Separator.svg',
  };

export function getPictogramAssetPath(machineType: ProductResponseDTO.MachineTypeEnum): string {
  const pictogram = machinePictogramMap[machineType] ?? machinePictogramMap.default;
  return `../../../assets/img/pictograms/${pictogram}`;
}

export const calculateTimeUtilizationData = (translate: TranslateService, utilization?: TimeCategory): ChartDataItem[] => {
  const categories = [
    { key: 'production', name: translate.instant('TIME-UTILIZATION.LABEL.PRODUCTION'), color: UTILIZATION_CATEGORY_COLORS.production },
    { key: 'cip', name: translate.instant('TIME-UTILIZATION.LABEL.CIP'), color: UTILIZATION_CATEGORY_COLORS.cip },
    { key: 'slowing_down', name: translate.instant('TIME-UTILIZATION.LABEL.SLOWING-DOWN'), color: UTILIZATION_CATEGORY_COLORS.slowing_down },
    { key: 'standstill', name: translate.instant('TIME-UTILIZATION.LABEL.STANDSTILL'), color: UTILIZATION_CATEGORY_COLORS.standstill },
    { key: 'startup', name: translate.instant('TIME-UTILIZATION.LABEL.STARTUP'), color: UTILIZATION_CATEGORY_COLORS.startup },
  ];

  const entries: ChartDataItem[] = categories.map(({ key, name, color }) => {
    const item = utilization?.[key as keyof TimeCategory];

    const duration = parseFloat(((item?.duration_in_seconds ?? 0) / 3600).toFixed(NUMBER_OF_DIGITS));
    const previousDuration = parseFloat(((item?.previous_duration_in_seconds ?? 0) / 3600).toFixed(NUMBER_OF_DIGITS));

    const percentage = parseFloat((item?.percentage ?? 0).toFixed(NUMBER_OF_DIGITS));
    const previousPercentage = parseFloat((item?.previous_percentage ?? 0).toFixed(NUMBER_OF_DIGITS));

    return {
      name,
      value: duration,
      percentage,
      color,
      change: parseFloat((duration - previousDuration).toFixed(NUMBER_OF_DIGITS)),
      changePercentage: previousPercentage,
      selected: true,
    };
  });

  const totalValue = entries.reduce((sum, e) => sum + e.value, 0);
  const totalChange = entries.reduce((sum, e) => sum + e.change, 0);
  const totalPercentage = entries.reduce((sum, e) => sum + e.percentage, 0);

  const changePercentage = totalChange !== 0 && totalValue !== 0 ? (totalChange / (totalValue - totalChange)) * 100 : 0;

  entries.push({
    name: translate.instant('WIDGET-LABEL.TOTAL'),
    value: parseFloat(totalValue.toFixed(NUMBER_OF_DIGITS)),
    percentage: parseFloat(totalPercentage.toFixed(NUMBER_OF_DIGITS)),
    color: PURE_BLACK,
    change: parseFloat(totalChange.toFixed(NUMBER_OF_DIGITS)),
    changePercentage: parseFloat(changePercentage.toFixed(NUMBER_OF_DIGITS)),
    selected: true,
    isTotal: true,
  });

  return entries;
};
